import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Search, Download, Chrome, ArrowRight, Star } from "lucide-react";
import ExtensionPopup from "./ExtensionPopup";
import OnboardingScreen from "./OnboardingScreen";
import { loadPreferences, savePreferences } from "./scoring";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRESET_BRANDS = [
  { label: "Patagonia", domain: "patagonia.com" },
  { label: "Eileen Fisher", domain: "eileenfisher.com" },
  { label: "H&M", domain: "hm.com" },
  { label: "Zara", domain: "zara.com" },
  { label: "Glossier", domain: "glossier.com" },
  { label: "L'Oréal", domain: "loreal.com" },
];

const MOCK_PRODUCT = {
  patagonia: {
    name: "Better Sweater Fleece Jacket",
    brand: "Patagonia",
    price: "$139.00",
    img: "https://images.unsplash.com/photo-1634393653736-98a63fb73742?w=300&q=80",
  },
  eileenfisher: {
    name: "Organic Cotton Turtleneck",
    brand: "Eileen Fisher",
    price: "$248.00",
    img: "https://images.unsplash.com/photo-1562157873-818bc0726f68?w=300&q=80",
  },
  default: {
    name: "Classic Essential",
    brand: "Brand",
    price: "$89.00",
    img: "https://images.unsplash.com/photo-1578587018452-892bacefd3f2?w=300&q=80",
  },
};

function getMockProduct(domain) {
  if (domain?.includes("patagonia")) return MOCK_PRODUCT.patagonia;
  if (domain?.includes("eileenfisher") || domain?.includes("eileen")) return MOCK_PRODUCT.eileenfisher;
  return { ...MOCK_PRODUCT.default, brand: domain?.split(".")[0] || "Brand" };
}

export default function CoralDemo() {
  const navigate = useNavigate();
  const [domain, setDomain] = useState("patagonia.com");
  const [inputValue, setInputValue] = useState("patagonia.com");
  const [brandData, setBrandData] = useState(null);
  const [alternatives, setAlternatives] = useState([]);
  const [loading, setLoading] = useState(false);
  const [preferences, setPreferences] = useState(loadPreferences());
  const [screen, setScreen] = useState("overlay"); // "overlay" | "onboarding"
  const [showPopup, setShowPopup] = useState(true);
  const [waitlistCount, setWaitlistCount] = useState(null);
  const [foundingEmail, setFoundingEmail] = useState("");
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [emailError, setEmailError] = useState("");

  const fetchBrand = useCallback(async (d) => {
    if (!d) return;
    setLoading(true);
    setBrandData(null);
    setShowPopup(true);
    try {
      const [brandRes, altRes] = await Promise.all([
        axios.get(`${API}/brand`, { params: { domain: d } }).catch(() => null),
        axios.get(`${API}/alternatives`, { params: { domain: d } }).catch(() => ({ data: { alternatives: [] } })),
      ]);
      if (brandRes) {
        setBrandData(brandRes.data);
      } else {
        setBrandData({ found: false });
      }
      setAlternatives(altRes?.data?.alternatives || []);
    } catch {
      setBrandData({ found: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBrand("patagonia.com");
  }, [fetchBrand]);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/waitlist/count`)
      .then((r) => r.json())
      .then((d) => setWaitlistCount(d.count))
      .catch(() => setWaitlistCount(0));
  }, []);

  function handleSearch(e) {
    e.preventDefault();
    let d = inputValue.trim().toLowerCase();
    if (!d) return;
    d = d.replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0];
    setDomain(d);
    fetchBrand(d);
  }

  function handlePreset(d) {
    setDomain(d);
    setInputValue(d);
    fetchBrand(d);
  }

  function handleSavePreferences(newPrefs) {
    setPreferences(newPrefs);
    setScreen("overlay");
  }

  const product = getMockProduct(domain);

  return (
    <div style={{ minHeight: "100vh", background: "#FAFAFA", fontFamily: "Inter, sans-serif" }}>
      {/* ── Nav ─────────────────────────────────────────────────────── */}
      <nav style={{
        background: "#fff",
        borderBottom: "1px solid var(--border)",
        padding: "0 24px",
        height: "56px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        position: "sticky",
        top: 0,
        zIndex: 100,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <img src="/coral-icon.webp" width="30" height="30" style={{ borderRadius: "8px" }} alt="Coral" />
          <span style={{ fontFamily: "Manrope", fontWeight: 800, fontSize: "17px", color: "var(--text-primary)" }}>
            coral
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <a href="#how-it-works" style={{ fontSize: "13px", color: "var(--text-secondary)", textDecoration: "none" }}>
            How it works
          </a>
          <button
            className="btn-coral"
            data-testid="get-extension-btn"
            onClick={() => document.getElementById('email-capture').scrollIntoView({ behavior: 'smooth' })}
            style={{ padding: "7px 14px", fontSize: "13px", borderRadius: "6px", gap: "5px" }}
          >
            <Chrome size={14} /> Get Early Access — Free Pro Forever
          </button>
        </div>
      </nav>

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <div style={{
        background: "#fff",
        padding: "48px 24px 40px",
        textAlign: "center",
        borderBottom: "1px solid var(--border)",
      }}>
        <div style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          background: "#FFF8F7",
          border: "1px solid #FAD7D4",
          borderRadius: "20px",
          padding: "4px 12px",
          marginBottom: "16px",
        }}>
          <Star size={11} fill="#FA7268" color="#FA7268" />
          <span style={{ fontSize: "12px", color: "var(--coral)", fontWeight: 600 }}>
            Free Chrome Extension
          </span>
        </div>
        <h1 style={{
          fontFamily: "Manrope",
          fontSize: "clamp(28px, 5vw, 48px)",
          fontWeight: 800,
          color: "var(--text-primary)",
          lineHeight: 1.15,
          marginBottom: "14px",
        }}>
          Shop ethically.<br />
          <span style={{ color: "#FA7268" }}>Every time.</span>
        </h1>
        <p style={{
          fontSize: "15px",
          color: "var(--text-secondary)",
          maxWidth: "440px",
          margin: "0 auto 16px",
          lineHeight: 1.6,
        }}>
          Coral scores brands on what matters to you — environment, labor, animals, and more.
          Your values, your score.
        </p>

        <div style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          background: "#FFF8F7",
          border: "1px solid #FAD7D4",
          borderRadius: "20px",
          padding: "4px 12px",
          marginBottom: "20px",
        }}>
          <span style={{ fontSize: "12px", color: "var(--coral)", fontWeight: 600 }}>
            Works where you already shop — Amazon · Target · Walmart · Sephora · Macy's · and 10,000+ brands
          </span>
        </div>

      </div>

      {/* ── Demo Section ─────────────────────────────────────────────── */}
      <div style={{
        maxWidth: "960px",
        margin: "0 auto",
        padding: "40px 24px",
        display: "flex",
        gap: "32px",
        alignItems: "flex-start",
        flexWrap: "wrap",
        justifyContent: "center",
      }}>
        {/* Mock browser / product page */}
        <div style={{
          flex: "1 1 380px",
          maxWidth: "480px",
          background: "#fff",
          border: "1px solid var(--border)",
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
        }} data-testid="mock-browser">
          {/* Browser chrome */}
          <div style={{
            background: "#F5F5F5",
            borderBottom: "1px solid var(--border)",
            padding: "10px 14px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}>
            <div style={{ display: "flex", gap: "5px" }}>
              {["#FF5F57", "#FFBD2E", "#28CA41"].map((c) => (
                <div key={c} style={{ width: "10px", height: "10px", borderRadius: "50%", background: c }} />
              ))}
            </div>
            <div style={{
              flex: 1,
              background: "#FFFFFF",
              border: "1px solid var(--border)",
              borderRadius: "6px",
              padding: "4px 10px",
              fontSize: "11px",
              color: "var(--text-secondary)",
            }}>
              amazon.com/Patagonia-Better-Sweater-Fleece
            </div>
            <div style={{
              width: "24px", height: "24px",
              borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", flexShrink: 0,
            }}
              onClick={() => setShowPopup(!showPopup)}
              data-testid="coral-icon-btn"
              title="Click to toggle Coral overlay"
            >
              <img src="/coral-icon.webp" width="24" height="24" style={{ borderRadius: "6px" }} alt="Coral" />
            </div>
          </div>

          {/* Product page */}
          <div style={{ padding: "20px" }}>
            <div style={{ display: "flex", gap: "16px", alignItems: "flex-start" }}>
              <img
                src={product.img}
                alt={product.name}
                style={{ width: "120px", height: "140px", objectFit: "cover", borderRadius: "8px", border: "1px solid var(--border)" }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "11px", color: "var(--text-tertiary)", marginBottom: "4px" }}>
                  {product.brand}
                </div>
                <h3 style={{ fontFamily: "Manrope", fontSize: "14px", fontWeight: 700, marginBottom: "8px", lineHeight: 1.3 }}>
                  {product.name}
                </h3>
                <div style={{ fontSize: "18px", fontWeight: 700, color: "var(--text-primary)", marginBottom: "12px" }}>
                  {product.price}
                </div>
                <button style={{
                  background: "#1A1A1A",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "8px 16px",
                  fontSize: "12px",
                  fontWeight: 600,
                  cursor: "pointer",
                  width: "100%",
                }}>
                  Add to Cart
                </button>
              </div>
            </div>

            <div style={{ marginTop: "16px", padding: "10px", background: "#FFF8F7", borderRadius: "8px", border: "1px solid #FAD7D4" }}>
              <p style={{ fontSize: "11px", color: "var(--text-secondary)" }}>
                Click the coral icon (<span style={{ color: "#FA7268" }}>🪸</span>) in the toolbar to see ethical ratings for this brand.
              </p>
            </div>
          </div>
        </div>

        {/* Coral overlay */}
        <div style={{ display: "flex", flexDirection: "column", gap: "10px", alignItems: "center" }}>
          {/* Screen switcher */}
          <div style={{
            display: "flex",
            background: "#fff",
            border: "1px solid var(--border)",
            borderRadius: "8px",
            padding: "3px",
            gap: "2px",
          }}>
            {["overlay", "onboarding"].map((s) => (
              <button
                key={s}
                data-testid={`screen-tab-${s}`}
                onClick={() => setScreen(s)}
                style={{
                  padding: "5px 12px",
                  borderRadius: "6px",
                  fontSize: "12px",
                  fontWeight: 500,
                  background: screen === s ? "#FA7268" : "transparent",
                  color: screen === s ? "#fff" : "var(--text-secondary)",
                  border: "none",
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                {s === "overlay" ? "Brand Score" : "My Values"}
              </button>
            ))}
          </div>

          {/* Popup */}
          <div className="fade-in" key={domain + screen}>
            {screen === "overlay" ? (
              <ExtensionPopup
                brandData={brandData}
                loading={loading}
                preferences={preferences}
                alternatives={alternatives}
                onOpenSettings={() => setScreen("onboarding")}
                onClose={null}
              />
            ) : (
              <OnboardingScreen
                preferences={preferences}
                onSave={handleSavePreferences}
                isSettings={preferences.onboardingComplete}
              />
            )}
          </div>
        </div>
      </div>

      {/* ── Founding Member Email Capture ──────────────────────────────── */}
      <div id="email-capture" style={{
        background: "#FFF5F4",
        borderBottom: "1px solid #FAD7D4",
        padding: "48px 24px",
        textAlign: "center",
        scrollMarginTop: "24px",
      }}>
        <div style={{ maxWidth: "480px", margin: "0 auto" }}>
          {waitlistCount === null || waitlistCount < 500 ? (
            <>
              <div style={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                background: "#FEECEB", border: "1px solid #FAD7D4",
                borderRadius: "20px", padding: "4px 12px", marginBottom: "16px",
              }}>
                <span style={{ fontSize: "12px", color: "#FA7268", fontWeight: 700 }}>First 500 only</span>
              </div>
              <h2 style={{
                fontFamily: "Manrope", fontWeight: 800,
                fontSize: "clamp(22px, 4vw, 32px)",
                color: "#1A1A1A", lineHeight: 1.2, marginBottom: "10px",
              }}>
                Become a Founding Member
              </h2>
              <p style={{ fontSize: "15px", fontWeight: 600, color: "#FA7268", marginBottom: "12px" }}>
                Get Coral Pro free, forever. First 500 slots only.
              </p>
              <p style={{
                fontSize: "14px", color: "#666", lineHeight: 1.65,
                maxWidth: "400px", margin: "0 auto 28px",
              }}>
                Founding members get unlimited saved brands, full certifier drilldown, and every Pro feature — free for life. No credit card. No catch.
              </p>
            </>
          ) : (
            <h2 style={{
              fontFamily: "Manrope", fontWeight: 700,
              fontSize: "clamp(18px, 3vw, 24px)",
              color: "#1A1A1A", lineHeight: 1.45, marginBottom: "24px",
            }}>
              Founding member slots are full. Enter your email to join the waitlist for launch updates.
            </h2>
          )}

          {emailSubmitted ? (
            <div style={{
              display: "inline-flex", alignItems: "center", gap: "8px",
              background: "#fff", border: "1px solid #28CA41",
              borderRadius: "10px", padding: "14px 24px",
              color: "#2F9E44", fontSize: "14px", fontWeight: 600,
            }}>
              ✓ You're on the list! We'll be in touch.
            </div>
          ) : (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!foundingEmail.trim()) return;
                setEmailError("");
                try {
                  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/waitlist`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ email: foundingEmail }),
                  });
                  if (res.ok) {
                    setEmailSubmitted(true);
                  } else {
                    setEmailError("Something went wrong — please try again.");
                  }
                } catch {
                  setEmailError("Something went wrong — please try again.");
                }
              }}
              style={{ display: "flex", gap: "8px", maxWidth: "420px", margin: "0 auto" }}
            >
              <input
                type="email"
                value={foundingEmail}
                onChange={(e) => setFoundingEmail(e.target.value)}
                placeholder="Enter your email"
                required
                data-testid="founding-email-input"
                style={{
                  flex: 1, padding: "11px 14px",
                  border: "1px solid #F0B8B4", borderRadius: "8px",
                  fontSize: "14px", outline: "none",
                  fontFamily: "Inter", background: "#fff",
                  transition: "border-color 0.15s",
                }}
                onFocus={(e) => e.target.style.borderColor = "#FA7268"}
                onBlur={(e) => e.target.style.borderColor = "#F0B8B4"}
              />
              <button
                type="submit"
                data-testid="founding-claim-btn"
                style={{
                  background: "#FA7268", color: "#fff", border: "none",
                  borderRadius: "8px", padding: "11px 20px",
                  fontSize: "14px", fontWeight: 600,
                  cursor: "pointer", whiteSpace: "nowrap", fontFamily: "Inter",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => e.target.style.background = "#E85F56"}
                onMouseLeave={(e) => e.target.style.background = "#FA7268"}
              >
                {waitlistCount !== null && waitlistCount >= 500 ? "Join the waitlist" : "Claim your spot →"}
              </button>
            </form>
          )}
          <p style={{ fontSize: "12px", color: "#999", marginTop: "12px" }}>
            🔒 No spam. Unsubscribe anytime.
          </p>
          {emailError && (
            <p style={{ fontSize: "13px", color: "#FA7268", marginTop: "8px", fontWeight: 500 }}>
              {emailError}
            </p>
          )}
        </div>
      </div>

      {/* ── How it works ─────────────────────────────────────────────── */}
      <div id="how-it-works" style={{
        background: "#fff",
        borderTop: "1px solid var(--border)",
        padding: "48px 24px",
        textAlign: "center",
      }}>
        <h2 style={{ fontFamily: "Manrope", fontSize: "24px", fontWeight: 700, marginBottom: "8px" }}>
          How Coral works
        </h2>
        <p style={{ fontSize: "14px", color: "var(--text-secondary)", marginBottom: "36px" }}>
          Three steps to smarter shopping
        </p>
        <div style={{
          display: "flex",
          gap: "24px",
          flexWrap: "wrap",
          justifyContent: "center",
          maxWidth: "700px",
          margin: "0 auto",
        }}>
          {[
            { num: "1", title: "Set your values", desc: "Tell us what matters to you — environment, labor rights, animal welfare and more." },
            { num: "2", title: "Shop anywhere", desc: "Visit any brand's website. Coral automatically detects the brand." },
            { num: "3", title: "See your score", desc: "A weighted score based on your priorities, not ours. Real data, real transparency." },
          ].map((step) => (
            <div key={step.num} style={{
              flex: "1 1 180px",
              maxWidth: "200px",
              textAlign: "center",
            }}>
              <div style={{
                width: "40px", height: "40px",
                background: "#FFF8F7",
                border: "2px solid #FA7268",
                borderRadius: "50%",
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 12px",
                fontFamily: "Manrope",
                fontSize: "16px",
                fontWeight: 700,
                color: "#FA7268",
              }}>
                {step.num}
              </div>
              <h3 style={{ fontFamily: "Manrope", fontSize: "14px", fontWeight: 700, marginBottom: "6px" }}>
                {step.title}
              </h3>
              <p style={{ fontSize: "12px", color: "var(--text-secondary)", lineHeight: 1.5 }}>
                {step.desc}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Install Section ───────────────────────────────────────────── */}
      <div id="install-section" style={{
        background: "#FFF8F7",
        borderTop: "1px solid #FAD7D4",
        padding: "48px 24px",
        textAlign: "center",
      }}>
        <div style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          background: "#FFF8F7",
          border: "1px solid #FAD7D4",
          borderRadius: "20px",
          padding: "4px 12px",
          marginBottom: "12px",
        }}>
          <Star size={11} fill="#FA7268" color="#FA7268" />
          <span style={{ fontSize: "12px", color: "var(--coral)", fontWeight: 600 }}>
            Coming Soon
          </span>
        </div>
        <h2 style={{ fontFamily: "Manrope", fontSize: "22px", fontWeight: 700, marginBottom: "8px" }}>
          Get the Chrome extension
        </h2>
        <p style={{ fontSize: "14px", color: "var(--text-secondary)", marginBottom: "24px" }}>
          Install it in 30 seconds. Free for everyone: composite score, domain scores, and Impact Lab. Coral Pro unlocks LGBTQ+ Equality and unlimited saved brands.
        </p>
        <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
        </div>
      </div>

      {/* Footer */}
      <div style={{
        background: "#fff",
        borderTop: "1px solid var(--border)",
        padding: "20px 24px",
        textAlign: "center",
        fontSize: "12px",
        color: "var(--text-tertiary)",
      }}>
        🪸 Coral — Shop ethically, every time. &nbsp;·&nbsp; Privacy-first. No accounts. No tracking. &nbsp;·&nbsp; <a href="/privacy" style={{ color: "var(--text-tertiary)", textDecoration: "none" }} onMouseOver={e => e.target.style.textDecoration = "underline"} onMouseOut={e => e.target.style.textDecoration = "none"}>Privacy Policy</a>
      </div>
    </div>
  );
}
